import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { adminAPI } from '../../api/bookings';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

import {
  getChipConfig, getSourceConfig,
  bruneiTodayISO, bruneiTomorrowISO,
} from './BookingsManagement/chipConfig';
import { bruneiDateOffset } from '../../lib/brunei';
import BookingCard from './BookingsManagement/BookingCard';
import FacetFilters from './BookingsManagement/FacetFilters';
import ActiveFilterBreadcrumb from './BookingsManagement/ActiveFilterBreadcrumb';
import { TodayGlance, TomorrowPreview } from './BookingsManagement/TimelineGlance';
import FiltersBar from './BookingsManagement/FiltersBar';
import MarkPaidDialog from './BookingsManagement/MarkPaidDialog';
import CancelBookingDialog from './BookingsManagement/CancelBookingDialog';
import Pagination from './BookingsManagement/Pagination';

// Default browsing window — mirrors the backend
// (booking_routes.py::_DEFAULT_LIST_WINDOW_DAYS) so the visible date
// inputs match what the API actually returns.
const DEFAULT_WINDOW_DAYS = 90;
const defaultDateFrom = () => bruneiDateOffset(-DEFAULT_WINDOW_DAYS);
const defaultDateTo = () => bruneiDateOffset(DEFAULT_WINDOW_DAYS);

const DEFAULT_PAGE_SIZE = 200;
// Debounce for text inputs (phone/email/booking_ref) — long enough to
// avoid a request-per-keystroke, short enough to feel responsive.
const TEXT_DEBOUNCE_MS = 300;

// URL sync — keeps the current filter state addressable & bookmarkable.
// Only writes params that differ from defaults so the URL stays clean.
function toSearchParams(f) {
  const p = new URLSearchParams();
  if (f.dateFrom && f.dateFrom !== defaultDateFrom()) p.set('date_from', f.dateFrom);
  if (f.dateTo && f.dateTo !== defaultDateTo()) p.set('date_to', f.dateTo);
  if (f.phone) p.set('phone', f.phone);
  if (f.email) p.set('email', f.email);
  if (f.bookingRef) p.set('ref', f.bookingRef);
  if (f.status && f.status !== 'ALL') p.set('status', f.status);
  if (f.source && f.source !== 'ALL') p.set('source', f.source);
  if (f.page && f.page !== 1) p.set('page', String(f.page));
  return p;
}

function fromSearchParams(sp) {
  return {
    dateFrom: sp.get('date_from') || defaultDateFrom(),
    dateTo: sp.get('date_to') || defaultDateTo(),
    phone: sp.get('phone') || '',
    email: sp.get('email') || '',
    bookingRef: sp.get('ref') || '',
    status: sp.get('status') || 'ALL',
    source: sp.get('source') || 'ALL',
    page: Math.max(1, parseInt(sp.get('page') || '1', 10)),
  };
}

export default function BookingsManagement() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter state is derived from the URL so refresh + deep-links Just Work.
  // `filters` is the *committed* state driving the API; `pending*` mirrors
  // hold the raw text-input values while debouncing.
  const [filters, setFilters] = useState(() => fromSearchParams(searchParams));
  const [pendingPhone, setPendingPhone] = useState(filters.phone);
  const [pendingEmail, setPendingEmail] = useState(filters.email);
  const [pendingRef, setPendingRef] = useState(filters.bookingRef);

  // Server response state.
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({
    by_status: { ALL: 0, AWAITING_PAYMENT: 0, CONFIRMED: 0, CANCELLED: 0, PAYMENT_FAILED: 0, RELEASED: 0 },
    by_source: { ALL: 0, CUSTOMER: 0, MANUAL_STAFF: 0, STAFF_BLOCK: 0, WAITLIST_CONVERSION: 0 },
  });
  const [glance, setGlance] = useState({
    today: { total: 0, CUSTOMER: 0, MANUAL_STAFF: 0, STAFF_BLOCK: 0, WAITLIST_CONVERSION: 0 },
    tomorrow: { total: 0, CONFIRMED: 0, AWAITING_PAYMENT: 0, PAYMENT_FAILED: 0, STAFF_BLOCK: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [staffMap, setStaffMap] = useState({});

  // ---- Debounce the three text inputs ----
  const debounceRef = useRef(null);
  useEffect(() => {
    if (
      pendingPhone === filters.phone
      && pendingEmail === filters.email
      && pendingRef === filters.bookingRef
    ) return; // no-op — avoids resetting the debounce clock on external filter updates
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // Commit the pending text filters. Page resets to 1 because the
      // result set is likely different.
      setFilters((f) => ({
        ...f,
        phone: pendingPhone,
        email: pendingEmail,
        bookingRef: pendingRef,
        page: 1,
      }));
    }, TEXT_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // Intentionally excludes filters.* — this effect only reacts to the
    // raw pending text inputs; committed `filters` changing (from this
    // same timeout, or from elsewhere) must NOT reset the debounce clock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPhone, pendingEmail, pendingRef]);

  // ---- Sync `filters` → URL search params (source of truth) ----
  useEffect(() => {
    const next = toSearchParams(filters);
    // Only write if actually different — avoids infinite re-renders and
    // spurious history entries.
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // Intentionally excludes searchParams/setSearchParams — this effect
    // only reacts to `filters` changing; including the URL-derived params
    // it also writes to risks re-triggering itself on every navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // ---- Handle inbound Dashboard navigation (once) ----
  useEffect(() => {
    if (!location.state?.filters) return;
    const { dateFrom: navFrom, dateTo: navTo, status } = location.state.filters;
    setFilters((f) => ({
      ...f,
      ...(navFrom ? { dateFrom: navFrom } : {}),
      ...(navTo ? { dateTo: navTo } : {}),
      ...(['CONFIRMED', 'AWAITING_PAYMENT', 'CANCELLED', 'PAYMENT_FAILED'].includes(status)
        ? { status }
        : {}),
      page: 1,
    }));
    // Clear the state so a subsequent refresh doesn't re-apply it.
    window.history.replaceState({}, '', window.location.pathname + window.location.search);
    // Deliberate mount-once effect — must NOT re-fire if location.state
    // changes later (e.g. from an in-page navigate() call), only on the
    // initial inbound navigation from the Dashboard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Load: rows + counts in parallel whenever filters change ----
  const buildQuery = useCallback((f) => ({
    date_from: f.dateFrom || undefined,
    date_to: f.dateTo || undefined,
    phone: f.phone || undefined,
    email: f.email || undefined,
    booking_ref: f.bookingRef || undefined,
    status: f.status,
    source: f.source,
    page: f.page,
    page_size: DEFAULT_PAGE_SIZE,
  }), []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const q = buildQuery(filters);
      // Rows + counts + glance parallelised — three round-trips fanned out.
      const [list, ct, gl] = await Promise.all([
        adminAPI.getBookings(q),
        adminAPI.getBookingsCounts({
          date_from: q.date_from, date_to: q.date_to,
          phone: q.phone, email: q.email, booking_ref: q.booking_ref,
        }),
        adminAPI.getBookingsGlance(),
      ]);
      setRows(list.rows || []);
      setTotal(list.total || 0);
      setCounts(ct);
      setGlance(gl);
    } catch (e) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [filters, buildQuery]);

  useEffect(() => { loadData(); }, [loadData]);

  // Staff lookup — one-shot, non-blocking.
  useEffect(() => {
    (async () => {
      const fallback = { 'admin-hardcoded-001': 'Rong Zhang' };
      try {
        const list = await adminAPI.getStaff();
        const map = { ...fallback };
        (list || []).forEach((s) => {
          if (s.id) map[s.id] = s.name || s.email || s.id.slice(0, 8);
        });
        setStaffMap(map);
      } catch {
        setStaffMap(fallback);
      }
    })();
  }, []);

  // ---------- filter setters (all reset page to 1) ----------
  const setDateFrom = (v) => setFilters(f => ({ ...f, dateFrom: v, page: 1 }));
  const setDateTo = (v) => setFilters(f => ({ ...f, dateTo: v, page: 1 }));
  const setStatusFilter = (v) => setFilters(f => ({ ...f, status: v, page: 1 }));
  const setSourceFilter = (v) => setFilters(f => ({ ...f, source: v, page: 1 }));
  const setPage = (v) => setFilters(f => ({ ...f, page: v }));

  const clearFilters = () => {
    const df = defaultDateFrom();
    const dt = defaultDateTo();
    setPendingPhone('');
    setPendingEmail('');
    setPendingRef('');
    setFilters({
      dateFrom: df, dateTo: dt,
      phone: '', email: '', bookingRef: '',
      status: 'ALL', source: 'ALL', page: 1,
    });
  };

  const onApply = () => {
    // Apply forces an immediate refetch even if nothing changed — useful
    // after backend mutations, or after date changes that don't route
    // through the debounce path.
    setFilters(f => ({ ...f, page: 1 }));
  };

  // ---------- glance quick-filter handlers ----------
  const applyTodayQuickFilter = (sourceKey) => {
    const today = bruneiTodayISO();
    setFilters({
      dateFrom: today, dateTo: today,
      phone: '', email: '', bookingRef: '',
      status: 'ALL', source: sourceKey, page: 1,
    });
    setPendingPhone(''); setPendingEmail(''); setPendingRef('');
  };

  const applyTomorrowQuickFilter = (bucketKey) => {
    const tomorrow = bruneiTomorrowISO();
    const next = {
      dateFrom: tomorrow, dateTo: tomorrow,
      phone: '', email: '', bookingRef: '',
      status: 'ALL', source: 'ALL', page: 1,
    };
    if (bucketKey === 'STAFF_BLOCK') next.source = 'STAFF_BLOCK';
    else next.status = bucketKey;
    setFilters(next);
    setPendingPhone(''); setPendingEmail(''); setPendingRef('');
  };

  // Cross-aware chip counts (post-facet) — served straight from the API.
  const statusCounts = useMemo(() => counts.by_status, [counts]);
  const sourceCounts = useMemo(() => counts.by_source, [counts]);
  const activeChip = getChipConfig(filters.status);

  // ---------- booking mutations ----------
  const [cancelTarget, setCancelTarget] = useState(null);
  const handleCancelBooking = (bookingId) => {
    const booking = rows.find((b) => b.id === bookingId);
    if (booking) setCancelTarget(booking);
  };
  const onCancelDialogDone = (voided, errMsg) => {
    if (errMsg) { toast.error(errMsg); return; }
    toast.success(voided ? 'Booking cancelled and Pocket Pay payment voided' : 'Booking cancelled');
    setCancelTarget(null);
    loadData();
  };

  const [markPaidTarget, setMarkPaidTarget] = useState(null);
  const handleMarkPaid = async (payload) => {
    if (!markPaidTarget) return;
    try {
      await adminAPI.markBookingPaid(markPaidTarget.id, payload);
      toast.success('Booking marked as paid — confirmation email sent');
      setMarkPaidTarget(null);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark booking paid');
    }
  };

  const handleReleaseSlot = async (booking) => {
    const reason = window.prompt(
      `Release this held booking?\n#${booking.id.slice(0, 8).toUpperCase()} — ${booking.booking_date} ${booking.time_slot}\n\nOptional note (visible to admins only):`,
      '',
    );
    if (reason === null) return;
    try {
      await adminAPI.releaseHeldBooking(booking.id, { reason: reason || undefined });
      toast.success('Slot released — courts are now free');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to release slot');
    }
  };

  const [reminderSendingId, setReminderSendingId] = useState(null);
  const handleSendReminder = async (booking) => {
    try {
      setReminderSendingId(booking.id);
      await adminAPI.sendPaymentReminder(booking.id);
      toast.success('Payment reminder sent to customer');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setReminderSendingId(null);
    }
  };

  return (
    <div data-testid="bookings-management-page">
      <TodayGlance stats={glance.today} onApply={applyTodayQuickFilter} />
      <TomorrowPreview stats={glance.tomorrow} onApply={applyTomorrowQuickFilter} />

      {/*
        Heading row.
        On wider screens the top pagination sits beside the "BOOKINGS"
        heading so operators can flip pages without scrolling to the
        bottom of a long list. On narrow screens it wraps under the
        heading (flex-wrap). Loading state and empty result sets hide
        the bar so it doesn't appear disconnected.
      */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-baseline gap-3">
          <h1 className="text-4xl font-black font-heading uppercase">Bookings</h1>
          <div className="text-sm text-muted-foreground" data-testid="bookings-result-summary">
            {loading ? 'Loading…' : `${rows.length} of ${total.toLocaleString()} shown`}
          </div>
        </div>
        {!loading && total > 0 && (
          <div className="flex-1 min-w-[380px] max-w-[720px]">
            <Pagination
              page={filters.page}
              pageSize={DEFAULT_PAGE_SIZE}
              total={total}
              onPageChange={setPage}
              variant="top"
            />
          </div>
        )}
      </div>

      <FiltersBar
        dateFrom={filters.dateFrom} setDateFrom={setDateFrom}
        dateTo={filters.dateTo} setDateTo={setDateTo}
        phoneFilter={pendingPhone} setPhoneFilter={setPendingPhone}
        bookingRefFilter={pendingRef} setBookingRefFilter={setPendingRef}
        emailFilter={pendingEmail} setEmailFilter={setPendingEmail}
        onApply={onApply}
        onClear={clearFilters}
      />

      <FacetFilters
        statusFilter={filters.status} setStatusFilter={setStatusFilter} statusCounts={statusCounts}
        sourceFilter={filters.source} setSourceFilter={setSourceFilter} sourceCounts={sourceCounts}
      />

      <ActiveFilterBreadcrumb
        statusFilter={filters.status} setStatusFilter={setStatusFilter}
        sourceFilter={filters.source} setSourceFilter={setSourceFilter}
      />

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center">
          <activeChip.icon className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
          <p className="text-muted-foreground">
            {total === 0
              ? 'No bookings match the current date range'
              : `No ${activeChip.label.toLowerCase()} bookings${filters.source !== 'ALL' ? ` (${getSourceConfig(filters.source).label})` : ''} match your filters`}
          </p>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {rows.map((booking) => (
              <BookingCard
                key={booking.id}
                booking={booking}
                staffMap={staffMap}
                onCancel={handleCancelBooking}
                onMarkPaid={setMarkPaidTarget}
                onRelease={handleReleaseSlot}
                onSendReminder={handleSendReminder}
                sendingReminder={reminderSendingId === booking.id}
              />
            ))}
          </div>
          <Pagination
            page={filters.page}
            pageSize={DEFAULT_PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}

      <MarkPaidDialog
        open={!!markPaidTarget}
        onOpenChange={(open) => { if (!open) setMarkPaidTarget(null); }}
        booking={markPaidTarget}
        onConfirm={handleMarkPaid}
      />

      {cancelTarget && (
        <CancelBookingDialog
          booking={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onCancelled={onCancelDialogDone}
        />
      )}
    </div>
  );
}
